// One fetch of the leaf grid, outside the store.
//
// Follows `rgipChatApi.ts` rather than the store pattern, for the same reason it
// exists: `src/store/index.ts` is being edited on another branch, and a page
// scoped read with no shared state does not need to be observable to earn its
// place there. The grid is fixed for a given model and tree revision, so it is
// cached per tab under both: keyed on the model alone, a tree edit in the same
// tab kept serving the grid from before it. Only a tree based model asks.
import axios from 'axios';

import { LeafGrid, PointsGrid } from './scoreLadder';

const cache = new Map<string, Promise<LeafGrid | null>>();

/**
 * The joint leaf membership for a model, or null when it has none to give.
 *
 * Null rather than a throw, and null rather than an empty grid. The route
 * answers with a 400 for a model that is not tree based, which is a fact about
 * the model and not a failure worth breaking the page over; the caller falls
 * back to the threshold number inputs. An empty grid would instead read as a
 * model with no leads and draw an empty control.
 */
export function fetchLeafGrid(
  tenant: string | number,
  modelId: string | number,
  treeRevision: number
): Promise<LeafGrid | null> {
  const key = `${tenant}/${modelId}/${treeRevision}`;
  if (!cache.has(key)) {
    cache.set(
      key,
      axios
        .get(`/api/tenant/${tenant}/model/${modelId}/ensembling/leaf_grid`)
        .then(({ data }) => (data?.rows?.length ? (data as LeafGrid) : null))
        .catch(() => null)
    );
  }
  return cache.get(key);
}

/**
 * A point based model's ladder source, or null when it has none to give.
 *
 * Not cached: it moves with the rules, which bump no revision this tab can see.
 * Each request re-scores the training set before grouping it, the same cost as
 * the leaf grid's, so it is fetched on mount and after a tree refresh only.
 */
export function fetchPointsGrid(
  tenant: string | number,
  modelId: string | number
): Promise<PointsGrid | null> {
  return axios
    .get(`/api/tenant/${tenant}/model/${modelId}/ensembling/points_grid`)
    .then(({ data }) => (data?.rows?.length ? (data as PointsGrid) : null))
    .catch(() => null);
}

/**
 * An engagement model's ladder source, or null when it has none to give.
 *
 * The same shape as the points grid, one row per score that occurs, so the
 * ladder is built by `pointsLadder` unchanged. Not cached, for the reason the
 * points grid is not: it moves with the event weights, which bump no revision
 * this tab can see.
 */
export function fetchScoreGrid(
  tenant: string | number,
  modelId: string | number
): Promise<PointsGrid | null> {
  return axios
    .get(`/api/tenant/${tenant}/model/${modelId}/ensembling/score_grid`)
    .then(({ data }) => (data?.rows?.length ? (data as PointsGrid) : null))
    .catch(() => null);
}
